
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import clsx from 'clsx'
import { NavLink } from 'react-router-dom'

const getUniqueAccessList = (options = [], requestedDomains = []) => {
  const merged = [...options, ...requestedDomains]
  const uniqueById = new Map()

  merged.forEach((item) => {
    if (item?.id && !uniqueById.has(item.id)) {
      uniqueById.set(item.id, item)
    }
  })

  return Array.from(uniqueById.values())
}

const ButtonEleDropdown = memo(
  ({
    option = [],
    dataRequestAccess = [],
    onHandleInstanceValue,
    whiteListDomainLoading,
    closeAllMenus,
  }) => {
    const uniqueAccessList = useMemo(
      () => getUniqueAccessList(option, dataRequestAccess),
      [option, dataRequestAccess]
    )

    const handleClick = useCallback(
      (event) => {
        if (onHandleInstanceValue) {
          onHandleInstanceValue(event)
        }

        closeAllMenus()
      },
      [onHandleInstanceValue, closeAllMenus]
    )

    return (
      <>
        {uniqueAccessList.map((domain) => {
          const alreadyRequested = dataRequestAccess.some(
            (element) => element?.id === domain?.id
          )

          return (
            <li key={domain.id}>
              <button
                type="button"
                value={domain.id}
                disabled={alreadyRequested || whiteListDomainLoading}
                className="btn-transparent"
                onClick={handleClick}
              >
                {domain.name}
              </button>
            </li>
          )
        })}
      </>
    )
  }
)

ButtonEleDropdown.displayName = 'ButtonEleDropdown'

const DropdownEle = memo(
  ({
    submenu,
    closeSidebarMenu,
    domains,
    onHandleInstanceValue,
    whiteListDomainLoading,
    onOpenModal,
  }) => {
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef(null)

    useEffect(() => {
      if (!isOpen) return undefined

      const handleOutsideClick = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false)
        }
      }

      document.addEventListener('mousedown', handleOutsideClick)
      document.addEventListener('touchstart', handleOutsideClick)

      return () => {
        document.removeEventListener('mousedown', handleOutsideClick)
        document.removeEventListener('touchstart', handleOutsideClick)
      }
    }, [isOpen])

    const handleToggle = useCallback(() => {
      setIsOpen((prev) => !prev)
    }, [])

    const closeAllMenus = useCallback(() => {
      setIsOpen(false)

      if (closeSidebarMenu) {
        closeSidebarMenu()
      }
    }, [closeSidebarMenu])

    const handleModalOpen = useCallback(() => {
      if (onOpenModal) {
        onOpenModal()
      }

      closeAllMenus()
    }, [onOpenModal, closeAllMenus])

    if (whiteListDomainLoading && submenu.buttonEle) {
      return <p style={{ fontSize: '14px' }}>Please wait...</p>
    }

    return (
      <div className="menu-dropdown" ref={dropdownRef}>
        <button
          type="button"
          className="dropdown-toggle"
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {submenu.title}
          <span className={clsx('icon-down')} />
        </button>

        {isOpen && (
          <ul className="dropdown-menu dropdown">
            {submenu.sub?.map((option) => {
              if (submenu.buttonEle) {
                return null
              }

              if (option?.adminRole === false) {
                return null
              }

              return (
                <li key={option.title || option.id}>
                  {option.isModalTrigger ? (
                    <button
                      className="menuItem"
                      type="button"
                      onClick={handleModalOpen}
                    >
                      {option.title}
                    </button>
                  ) : (
                    <NavLink
                      to={option.path}
                      className={({ isActive }) =>
                        `menuItem ${isActive ? 'active' : ''}`
                      }
                      onClick={closeAllMenus}
                    >
                      {option.title}
                    </NavLink>
                  )}
                </li>
              )
            })}

            {submenu.buttonEle && (
              <ButtonEleDropdown
                dataRequestAccess={domains}
                option={submenu.sub || []}
                onHandleInstanceValue={onHandleInstanceValue}
                whiteListDomainLoading={whiteListDomainLoading}
                closeAllMenus={closeAllMenus}
              />
            )}
          </ul>
        )}
      </div>
    )
  }
)

DropdownEle.displayName = 'DropdownEle'

const MenuItem = ({
  navbarItem = [],
  onHandleInstanceValue,
  whiteListDomainLoading,
  domains = [],
  closeSidebarMenu,
  onOpenModal,
}) => {
  return (
    <>
      {navbarItem.map((item) =>
        item.sub ? (
          <DropdownEle
            key={item.title}
            submenu={item}
            domains={domains}
            onHandleInstanceValue={onHandleInstanceValue}
            whiteListDomainLoading={whiteListDomainLoading}
            closeSidebarMenu={closeSidebarMenu}
            onOpenModal={onOpenModal}
          />
        ) : (
          <NavLink
            key={item.title}
            to={item.path}
            className={({ isActive }) =>
              `menuItem ${isActive ? 'active' : 'unselected'}`
            }
            onClick={closeSidebarMenu}
          >
            {item.title}
          </NavLink>
        )
      )}
    </>
  )
}

export default memo(MenuItem)